import { Box } from '@mui/material';
import { DataGridPro, DataGridProProps, GridColDef, GridToolbar } from '@mui/x-data-grid-pro';
import { parseISO } from 'date-fns';
import React from 'react';

import { commaSepPasteFilter } from 'app/components/csv-operator';
import { CategoryImpact } from 'app/model/redshift/impact-report/category-impact';
import { ChannelImpact } from 'app/model/redshift/impact-report/channel-impact';
import { StoreImpact } from 'app/model/redshift/impact-report/store-impact';
import { DataGridFormatters } from 'app/utils/data-grid-formatters';

interface ImpactReportGridProps
    extends Omit<DataGridProProps<CategoryImpact | ChannelImpact | StoreImpact>, 'columns'> {
    type: 'category' | 'channel' | 'store';
}

export const ImpactReportGrid: React.FunctionComponent<ImpactReportGridProps> = ({ type, ...rest }) => {
    const columns: Array<GridColDef<CategoryImpact | ChannelImpact | StoreImpact>> = [];

    switch (type) {
        case 'category':
            columns.push(...(categoryColumns as Array<GridColDef<CategoryImpact | ChannelImpact | StoreImpact>>));
            break;
        case 'channel':
            columns.push(...(channelColumns as Array<GridColDef<CategoryImpact | ChannelImpact | StoreImpact>>));
            break;
        case 'store':
            columns.push(...(storeColumns as Array<GridColDef<CategoryImpact | ChannelImpact | StoreImpact>>));
            break;
        default:
            throw new Error('Unexpected type');
    }

    columns.push(...commonColumns);

    return (
        <Box sx={{ width: '100%', display: 'grid', height: '100%' }}>
            <DataGridPro
                components={{ Toolbar: GridToolbar }}
                {...rest}
                columns={columns}
                columnVisibilityModel={{ id: false }}
            />
        </Box>
    );
};

const categoryColumns: Array<GridColDef<CategoryImpact>> = [
    {
        field: 'preprod.catId',
        headerName: 'PreP Cat Id',
        type: 'number',
        valueGetter: (params) => params.row.preprod.catId,
        flex: 1,
    },
    {
        field: 'preprod.category',
        headerName: 'PreP Category',
        valueGetter: (params) => params.row.preprod.category,
        flex: 1,
    },

    {
        field: 'prod.catId',
        headerName: 'Prod Cat Id',
        type: 'number',
        valueGetter: (params) => params.row.prod.catId,
        flex: 1,
    },
    {
        field: 'prod.category',
        headerName: 'Prod Category',
        valueGetter: (params) => params.row.prod.category,
        flex: 1,
    },
];

const channelColumns: Array<GridColDef<ChannelImpact>> = [
    {
        field: 'preprod.channel',
        headerName: 'Cur Channel',
        type: 'string',
        valueGetter: (params) => params.row.preprod.channel,
        flex: 1,
    },
    {
        field: 'prod.channel',
        headerName: 'Ref Channel',
        type: 'string',
        valueGetter: (params) => params.row.prod.channel,
        flex: 1,
    },
    {
        field: 'preprod.storeType',
        headerName: 'Cur Store Type',
        type: 'string',
        valueGetter: (params) => params.row.preprod.storeType,
        flex: 1,
    },
    {
        field: 'prod.storeType',
        headerName: 'Ref Store Type',
        type: 'string',
        valueGetter: (params) => params.row.prod.storeType,
        flex: 1,
    },
];

const storeColumns: Array<GridColDef<StoreImpact>> = [
    {
        field: 'preprod.storeId',
        headerName: 'PreP Id',
        type: 'number',
        filterOperators: commaSepPasteFilter,
        valueGetter: (params) => params.row.preprod.storeId,
        flex: 1,
    },
    {
        field: 'preprod.storeName',
        headerName: 'PreP Name',
        valueGetter: (params) => params.row.preprod.storeName,
        flex: 1,
    },
    {
        field: 'preprod.storeType',
        headerName: 'PreP Type',
        valueGetter: (params) => params.row.preprod.storeType,
        flex: 1,
    },
    {
        field: 'preprod.channel',
        headerName: 'PreP Channel',
        valueGetter: (params) => params.row.preprod.channel,
        flex: 1,
    },
    {
        field: 'preprod.category',
        headerName: 'PreP Cat',
        type: 'number',
        valueGetter: (params) => params.row.preprod.category,
        flex: 1,
    },
    {
        field: 'preprod.categoryName',
        headerName: 'PreP Cat Name',
        valueGetter: (params) => params.row.preprod.categoryName,
        flex: 1,
    },
    {
        field: 'prod.storeId',
        headerName: 'Prod Id',
        type: 'number',
        filterOperators: commaSepPasteFilter,
        valueGetter: (params) => params.row.prod.storeId,
        flex: 1,
    },
    {
        field: 'prod.storeName',
        headerName: 'Prod Name',
        valueGetter: (params) => params.row.prod.storeName,
        flex: 1,
    },
    {
        field: 'prod.storeType',
        headerName: 'Prod Type',
        valueGetter: (params) => params.row.prod.storeType,
        flex: 1,
    },
    {
        field: 'prod.channel',
        headerName: 'Prod Channel',
        valueGetter: (params) => params.row.prod.channel,
        flex: 1,
    },
    {
        field: 'prod.category',
        headerName: 'Prod Cat',
        valueGetter: (params) => params.row.prod.category,
        flex: 1,
    },
    {
        field: 'prod.categoryName',
        headerName: 'Prod Cat Name',
        valueGetter: (params) => params.row.prod.categoryName,
        flex: 1,
    },
];

const commonColumns: Array<GridColDef<CategoryImpact | ChannelImpact | StoreImpact>> = [
    {
        field: 'id',
        headerName: 'ID',
        type: 'number',
        flex: 1,
    },
    {
        field: 'trans',
        headerName: 'Transactions',
        type: 'number',
        valueFormatter: DataGridFormatters.numberLocaleFormatter,
        flex: 1,
    },
    {
        field: 'mids',
        headerName: 'MIDs',
        type: 'number',
        valueFormatter: DataGridFormatters.numberLocaleFormatter,
        flex: 1,
    },
    {
        field: 'spend',
        headerName: 'Cur Spend',
        type: 'number',
        valueFormatter: DataGridFormatters.currencyLocaleFormatter,
        flex: 1,
    },
    {
        field: 'prodSpendTotal',
        headerName: 'Ref Spend',
        type: 'number',
        valueFormatter: DataGridFormatters.currencyLocaleFormatter,
        flex: 1,
    },
    {
        field: 'pctProdAffected',
        headerName: '% Affected',
        type: 'number',
        valueFormatter: DataGridFormatters.numberLocaleFormatterFn({
            maximumFractionDigits: 4,
            minimumFractionDigits: 4,
        }),
        flex: 1,
    },
    {
        field: 'runDate',
        headerName: 'Run Date',
        type: 'date',
        valueFormatter: (params) => (params.value ? parseISO(params.value as string).toLocaleDateString() : ''),
        flex: 1,
    },
];
